/**
 * Thumbnail Upload Utility
 * 
 * Extracts a thumbnail from the first video/image overlay and uploads it to Convex storage.
 */

import { MediaBunnyThumbnailService } from '../../services/media-bunny-thumbnail-service';

export interface ThumbnailUploadResult {
    storageId: string;
    success: boolean;
    error?: string;
}

/**
 * Extract a thumbnail from the first video/image in the overlays
 * and upload it to Convex storage
 */
export async function extractAndUploadThumbnail(
    overlays: any[],
    generateUploadUrl: () => Promise<string>,
): Promise<ThumbnailUploadResult | null> {
    try {
        // Find the first video or image overlay
        const mediaOverlay = overlays.find(
            (overlay) =>
                overlay.type === 'video' ||
                overlay.type === 'image'
        );

        if (!mediaOverlay) {
            console.log('[ThumbnailUpload] No video/image overlay found');
            return null;
        }

        // Get the source URL
        const src = mediaOverlay.data?.src || mediaOverlay.src;
        if (!src) {
            console.log('[ThumbnailUpload] No source found in overlay');
            return null;
        }

        console.log('[ThumbnailUpload] Extracting thumbnail from:', src.substring(0, 50) + '...');

        let thumbnailDataUrl: string = '';

        if (mediaOverlay.type === 'video') {
            // For video, try to extract a frame
            thumbnailDataUrl = await extractVideoThumbnail(src);
        } else if (mediaOverlay.type === 'image') {
            // For image, resize and convert to thumbnail
            thumbnailDataUrl = await extractImageThumbnail(src);
        }

        if (!thumbnailDataUrl) {
            console.log('[ThumbnailUpload] Failed to extract thumbnail');
            return null;
        }

        // Convert data URL to blob
        const response = await fetch(thumbnailDataUrl);
        const blob = await response.blob();

        // Get upload URL from Convex
        const uploadUrl = await generateUploadUrl();

        // Upload the thumbnail
        const uploadResponse = await fetch(uploadUrl, {
            method: 'POST',
            headers: {
                'Content-Type': blob.type || 'image/jpeg',
            },
            body: blob,
        });

        if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status}`);
        }

        const { storageId } = await uploadResponse.json();

        console.log('[ThumbnailUpload] Thumbnail uploaded successfully:', storageId);

        return {
            storageId,
            success: true,
        };
    } catch (error) {
        console.error('[ThumbnailUpload] Error:', error);
        return {
            storageId: '',
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
}

/**
 * Extract a thumbnail from a video URL
 */
async function extractVideoThumbnail(src: string): Promise<string> {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';

        const timeoutId = setTimeout(() => {
            console.warn('[ThumbnailUpload] Video loading timed out');
            resolve('');
        }, 10000);

        video.onloadedmetadata = () => {
            // Seek to 1 second or middle of video
            video.currentTime = Math.min(1, video.duration / 2);
        };

        video.onseeked = () => {
            clearTimeout(timeoutId);
            try {
                const canvas = document.createElement('canvas');
                // Use a good thumbnail size
                canvas.width = 320;
                canvas.height = 180;

                const ctx = canvas.getContext('2d');
                if (ctx) {
                    // Calculate aspect ratio to maintain proportions
                    const videoAspect = video.videoWidth / video.videoHeight;
                    const canvasAspect = canvas.width / canvas.height;

                    let drawWidth = canvas.width;
                    let drawHeight = canvas.height;
                    let offsetX = 0;
                    let offsetY = 0;

                    if (videoAspect > canvasAspect) {
                        // Video is wider - fit to height
                        drawWidth = canvas.height * videoAspect;
                        offsetX = (canvas.width - drawWidth) / 2;
                    } else {
                        // Video is taller - fit to width
                        drawHeight = canvas.width / videoAspect;
                        offsetY = (canvas.height - drawHeight) / 2;
                    }

                    // Fill with black background first
                    ctx.fillStyle = '#000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                } else {
                    resolve('');
                }
            } catch (error) {
                console.error('[ThumbnailUpload] Error drawing video frame:', error);
                resolve('');
            } finally {
                URL.revokeObjectURL(video.src);
            }
        };

        video.onerror = () => {
            clearTimeout(timeoutId);
            console.error('[ThumbnailUpload] Error loading video');
            resolve('');
        };

        // Handle both blob URLs and regular URLs
        if (src.startsWith('blob:')) {
            video.src = src;
        } else {
            video.src = src;
        }
    });
}

/**
 * Extract a thumbnail from an image URL
 */
async function extractImageThumbnail(src: string): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        const timeoutId = setTimeout(() => {
            console.warn('[ThumbnailUpload] Image loading timed out');
            resolve('');
        }, 10000);

        img.onload = () => {
            clearTimeout(timeoutId);
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 320;
                canvas.height = 180;

                const ctx = canvas.getContext('2d');
                if (ctx) {
                    // Calculate aspect ratio to maintain proportions
                    const imgAspect = img.width / img.height;
                    const canvasAspect = canvas.width / canvas.height;

                    let drawWidth = canvas.width;
                    let drawHeight = canvas.height;
                    let offsetX = 0;
                    let offsetY = 0;

                    if (imgAspect > canvasAspect) {
                        // Image is wider - fit to height
                        drawWidth = canvas.height * imgAspect;
                        offsetX = (canvas.width - drawWidth) / 2;
                    } else {
                        // Image is taller - fit to width
                        drawHeight = canvas.width / imgAspect;
                        offsetY = (canvas.height - drawHeight) / 2;
                    }

                    // Fill with black background first
                    ctx.fillStyle = '#000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                } else {
                    resolve('');
                }
            } catch (error) {
                console.error('[ThumbnailUpload] Error drawing image:', error);
                resolve('');
            }
        };

        img.onerror = () => {
            clearTimeout(timeoutId);
            console.error('[ThumbnailUpload] Error loading image');
            resolve('');
        };

        img.src = src;
    });
}
